import type { Mon } from '../generated/mon';

// 12 facets, ids 1..12 (0 = none). Boost-indexed magnitudes (mirror
// Facets.sol::_facetPctsForBoost): the boost stat
// determines both the boost% and the cost% paid on the nerfed stat.
// HP/Atk/Def boosts are symmetric (+5%/-5%); speed boosts pay a heavier
// -10% cost so they can't cheaply break speed ties. Atk/Def each shift
// both physical and special variants.

export const TOTAL_FACETS = 12;
export const FACET_UNLOCKED_MASK = 0xfff;

export type StatGroup = 'HP' | 'Atk' | 'Def' | 'Speed';

export interface FacetDef {
  readonly id: number;
  readonly name: string;
  readonly glyph: string;
  readonly boost: StatGroup;
  readonly nerf: StatGroup;
  readonly lore: string;
}

// Order matches Facets.sol::_facetDef. Glyphs are placeholder unicode until
// the sprite-sheet art lands.
export const FACETS: ReadonlyArray<FacetDef> = [
  {
    id: 1,
    name: 'Abundant',
    glyph: 'Ѻ',
    boost: 'HP',
    nerf: 'Atk',
    lore: 'Bounty before blows.',
  },
  {
    id: 2,
    name: 'Benevolent',
    glyph: '✚',
    boost: 'HP',
    nerf: 'Def',
    lore: 'Generosity before preservation.',
  },
  {
    id: 3,
    name: 'Corpulent',
    glyph: '◉',
    boost: 'HP',
    nerf: 'Speed',
    lore: 'Mass before wit.',
  },
  {
    id: 4,
    name: 'Desolate',
    glyph: '⋒',
    boost: 'Atk',
    nerf: 'HP',
    lore: 'Strength before vitality.',
  },
  {
    id: 5,
    name: 'Relentless',
    glyph: '⚔',
    boost: 'Atk',
    nerf: 'Def',
    lore: 'Swords before shields.',
  },
  {
    id: 6,
    name: 'Inexorable',
    glyph: '➤',
    boost: 'Atk',
    nerf: 'Speed',
    lore: 'Offense before rhythm.',
  },
  {
    id: 7,
    name: 'Warded',
    glyph: '✜',
    boost: 'Def',
    nerf: 'HP',
    lore: 'Bulk before health.',
  },
  { id: 8, name: 'Stoic', glyph: '⚖', boost: 'Def', nerf: 'Atk', lore: 'Resistance before anger.' },
  {
    id: 9,
    name: 'Ossified',
    glyph: '▣',
    boost: 'Def',
    nerf: 'Speed',
    lore: 'Steel before swiftness.',
  },
  {
    id: 10,
    name: 'Meteoric',
    glyph: '៙',
    boost: 'Speed',
    nerf: 'HP',
    lore: 'Alacrity before heart.',
  },
  {
    id: 11,
    name: 'Elusive',
    glyph: '◇',
    boost: 'Speed',
    nerf: 'Atk',
    lore: 'Discretion over dispute.',
  },
  {
    id: 12,
    name: 'Unfettered',
    glyph: '⊰',
    boost: 'Speed',
    nerf: 'Def',
    lore: 'Haste over haleness',
  },
];

export const FACETS_BY_ID: ReadonlyMap<number, FacetDef> = new Map(
  FACETS.map((f) => [f.id, f])
);

export function getFacet(facetId: number): FacetDef | undefined {
  return FACETS_BY_ID.get(facetId);
}

export type FacetFamily = 'hp' | 'atk' | 'def' | 'spd';

export function facetFamily(boost: StatGroup): FacetFamily {
  return boost === 'HP'
    ? 'hp'
    : boost === 'Atk'
    ? 'atk'
    : boost === 'Def'
    ? 'def'
    : 'spd';
}

export function isFacetUnlocked(
  unlockedBitmap: number,
  facetId: number
): boolean {
  if (facetId < 1 || facetId > TOTAL_FACETS) return false;
  return (unlockedBitmap & (1 << (facetId - 1))) !== 0;
}

export function listUnlockedFacets(unlockedBitmap: number): number[] {
  const out: number[] = [];
  for (let i = 1; i <= TOTAL_FACETS; i++) {
    if (isFacetUnlocked(unlockedBitmap, i)) out.push(i);
  }
  return out;
}

export interface StatDelta {
  hp: number;
  attack: number;
  defense: number;
  specialAttack: number;
  specialDefense: number;
  speed: number;
}

export const EMPTY_STAT_DELTA: StatDelta = Object.freeze({
  hp: 0,
  attack: 0,
  defense: 0,
  specialAttack: 0,
  specialDefense: 0,
  speed: 0,
});

// Boost-indexed percentages (mirror Facets.sol::_facetPctsForBoost).
const BOOST_PCT: Readonly<Record<StatGroup, number>> = { HP: 5, Atk: 5, Def: 5, Speed: 5 };
const COST_PCT:  Readonly<Record<StatGroup, number>> = { HP: 5, Atk: 5, Def: 5, Speed: 10 };

export function facetBoostPct(boost: StatGroup): number {
  return BOOST_PCT[boost];
}

export function facetCostPct(boost: StatGroup): number {
  return COST_PCT[boost];
}

const STAT_SHORT: Readonly<Record<StatGroup, string>> = { HP: 'HP', Atk: 'ATK', Def: 'DEF', Speed: 'SPD' };

export interface FacetEffect {
  boostPct: number;
  boostStat: string;
  nerfPct: number;
  nerfStat: string;
}

export function facetEffect(facet: FacetDef): FacetEffect {
  return {
    boostPct: facetBoostPct(facet.boost),
    boostStat: STAT_SHORT[facet.boost],
    nerfPct: facetCostPct(facet.boost),
    nerfStat: STAT_SHORT[facet.nerf],
  };
}

// Floor-divide to match the Solidity `uint256 * pct / 100` truncation.
function pctOf(base: number, pct: number): number {
  return Math.floor((base * pct) / 100);
}

function applyGroup(
  delta: StatDelta,
  group: StatGroup,
  base: Mon['stats'],
  isBoost: boolean,
  pct: number,
): void {
  const sign = isBoost ? 1 : -1;
  switch (group) {
    case 'HP':
      delta.hp = sign * pctOf(base.hp, pct);
      break;
    case 'Speed':
      delta.speed = sign * pctOf(base.speed, pct);
      break;
    case 'Atk': {
      delta.attack = sign * pctOf(base.attack, pct);
      delta.specialAttack = sign * pctOf(base.specialAttack, pct);
      break;
    }
    case 'Def': {
      delta.defense = sign * pctOf(base.defense, pct);
      delta.specialDefense = sign * pctOf(base.specialDefense, pct);
      break;
    }
  }
}

export function computeFacetDelta(
  base: Mon['stats'],
  facetId: number
): StatDelta {
  if (facetId < 1 || facetId > TOTAL_FACETS) return { ...EMPTY_STAT_DELTA };
  const facet = FACETS_BY_ID.get(facetId)!;
  const delta: StatDelta = {
    hp: 0,
    attack: 0,
    defense: 0,
    specialAttack: 0,
    specialDefense: 0,
    speed: 0,
  };
  applyGroup(delta, facet.boost, base, true, BOOST_PCT[facet.boost]);
  applyGroup(delta, facet.nerf, base, false, COST_PCT[facet.boost]);
  return delta;
}

// Mirrors GachaTeamRegistry.sol::_levelForExp (skipped by the chomp
// transpiler so we shadow the 12-step lookup table here). Index = level,
// value = cumulative EXP at which that level begins. Caps at 12.
export const LV_THRESHOLDS: ReadonlyArray<number> = [
  0, 4, 10, 18, 28, 40, 54, 70, 88, 108, 130, 154, 180,
];
export const MAX_LEVEL = LV_THRESHOLDS.length - 1;

export function levelForExp(exp: number): number {
  for (let i = 1; i < LV_THRESHOLDS.length; i++) {
    if (exp < LV_THRESHOLDS[i]) return i - 1;
  }
  return MAX_LEVEL;
}

export function thresholdFor(level: number): number {
  if (level <= 0) return 0;
  if (level >= MAX_LEVEL) return LV_THRESHOLDS[MAX_LEVEL];
  return LV_THRESHOLDS[level];
}

export function progressInLevel(exp: number, level: number): number {
  if (level >= MAX_LEVEL) return 1;
  const lo = thresholdFor(level);
  const hi = thresholdFor(level + 1);
  if (hi <= lo) return 1;
  return Math.max(0, Math.min(1, (exp - lo) / (hi - lo)));
}

type LeveledMon = { level?: number | null; exp?: number | null };

export function expProgressPct(mon: LeveledMon | null | undefined): number {
  if (!mon || mon.level == null || mon.exp == null) return 0;
  return progressInLevel(mon.exp, mon.level);
}

export function expProgressLabel(mon: LeveledMon | null | undefined): string {
  if (!mon || mon.level == null || mon.exp == null) return '0';
  if (mon.level >= MAX_LEVEL) return 'MAX';
  return `${mon.exp}/${thresholdFor(mon.level + 1)}`;
}

export function applyFacetToStats(
  base: Mon['stats'],
  facetId: number
): Mon['stats'] {
  const d = computeFacetDelta(base, facetId);
  return {
    hp: base.hp + d.hp,
    attack: base.attack + d.attack,
    defense: base.defense + d.defense,
    specialAttack: base.specialAttack + d.specialAttack,
    specialDefense: base.specialDefense + d.specialDefense,
    speed: base.speed + d.speed,
  };
}
